import React, {useCallback, useEffect, useState} from 'react';
import {PersistGate} from 'redux-persist/integration/react';
import {store} from './redux-store/store';
import {persistor, useAppSelector} from './redux-store/store';
import Router from './router';
import {ThemeProvider} from '@mui/material/styles';
import {
  lightTheme as sportJoeLightTheme,
  darkTheme as sportJoeDarkTheme
} from '@/assets/styles/sportjoe-theme';
import {
  lightTheme as sjoneLightTheme,
  darkTheme as sjoneDarkTheme
} from '@/assets/styles/sjone-theme';
import {GlobalStyles, CssBaseline} from '@mui/material';
import {I18nextProvider} from 'react-i18next';
import i18n from '@/i18n/i18n';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import de from 'date-fns/locale/de';
import {LocalizationProvider} from '@mui/x-date-pickers-pro';
import {Theme} from '@mui/material/styles';
import {useMemo} from 'react';
import {Provider as ReduxProvider, useDispatch} from 'react-redux';
import {Helmet, HelmetProvider} from 'react-helmet-async';
import {useTranslation} from 'react-i18next';
import {SnackbarProvider} from '@/contexts/snackbar';
import {env} from './config';
import '@/styles.css';
import LoadingScreen from './pages/loading-screen';
import {setCompanySportsLoaded, stopLoading} from './redux-store/slices/auth';
import {CompanyProvider} from './contexts/admin/companies';
import {MatrixProvider} from './contexts/matrix/client/provider';
import ChatItem from './components/matrix-chat/ui/chat-item';
import {findSports} from './rest/sports/find-sports';
import {setSports} from './redux-store/slices/sports';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _mtm: Array<{[key: string]: any}>;
  }
}

function App() {
  return (
    <>
      <ReduxProvider store={store}>
        <PersistGate
          loading={<LoadingScreen />}
          persistor={persistor}
          onBeforeLift={() => {
            store.dispatch(stopLoading());
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
            <I18nextProvider i18n={i18n}>
              <>
                <AppWithStore />
              </>
            </I18nextProvider>
          </LocalizationProvider>
        </PersistGate>
      </ReduxProvider>
    </>
  );
}

function AppWithStore() {
  const {t} = useTranslation();

  const {APP_VARIANT, THEME_VARIANT, MATOMO_CONTAINER_URL} = env;

  const darkMode = useAppSelector((state) => state.theme.darkMode);
  const companyId = useAppSelector((state) => state.company.company._id);
  const userInfos = useAppSelector((state) => state.userInfos.userInfos);
  const {sports} = useAppSelector((state) => state.sports);
  const {companySportsLoaded, keycloakReady} = useAppSelector(
    (state) => state.auth
  );
  const [roomId, setRoomId] = useState<string | null>(null);
  const [shouldOpenDirectly, setShouldOpenDirectly] = useState(false);

  const dispatch = useDispatch();

  const chosenTheme = useMemo(() => {
    const themeVariant = THEME_VARIANT;
    if (themeVariant === 'sjone') {
      return darkMode ? sjoneDarkTheme : sjoneLightTheme;
    }
    return darkMode ? sportJoeDarkTheme : sportJoeLightTheme;
  }, [THEME_VARIANT, darkMode]);

  const handleWindowClose = useCallback(() => {
    setShouldOpenDirectly(false);
    setRoomId(null);
  }, []);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const roomId = event.data.roomId;
        if (roomId) {
          setRoomId(roomId);
          setShouldOpenDirectly(true);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!window._mtm) {
      window._mtm = [];
    }

    window._mtm.push({
      'mtm.startTime': new Date().getTime(),
      event: 'mtm.Start'
    });

    const d = document;
    const g = d.createElement('script');
    const s = d.getElementsByTagName('script')[0];
    g.async = true;
    g.src = MATOMO_CONTAINER_URL;

    if (s && s.parentNode) {
      s.parentNode.insertBefore(g, s);
    } else {
      console.warn('No matomo script found');
    }
  }, [MATOMO_CONTAINER_URL]);

  useEffect(() => {
    if (Object.keys(sports).length === 0 && !companySportsLoaded) {
      findSports()
        .then((sports) => {
          dispatch(setSports(sports));
        })
        .catch((error) => {
          console.error('Error fetching sports: ', error);
        });
    }
    if (APP_VARIANT === 'sjone' && keycloakReady && !companySportsLoaded) {
      const companyIdToUseAsParam =
        userInfos.companies?.length && userInfos.companies[0].id._id;
      if (companyIdToUseAsParam) {
        findSports(companyIdToUseAsParam)
          .then((sports) => {
            dispatch(setSports(sports));
            dispatch(setCompanySportsLoaded(true));
          })
          .catch((error) => {
            console.error('Error fetching sports: ', error);
          });
      }
    }
  }, [
    APP_VARIANT,
    companyId,
    companySportsLoaded,
    dispatch,
    keycloakReady,
    sports,
    userInfos
  ]);

  return (
    <HelmetProvider>
      <ThemeProvider theme={chosenTheme}>
        <StyleOverride theme={chosenTheme} />
        <CssBaseline />
        <SnackbarProvider>
          <CompanyProvider companyId={companyId}>
            <MatrixProvider>
              <Helmet>
                <title>
                  {APP_VARIANT === 'sportjoe'
                    ? `SportJoe - ${t('sports_community')}`
                    : `one - Active Team - ${t('one_description')}`}
                </title>
              </Helmet>
              {roomId && shouldOpenDirectly && (
                <ChatItem
                  roomId={roomId}
                  shouldOpenDirectly={shouldOpenDirectly}
                  onWindowClose={handleWindowClose}
                />
              )}
              <Router />
            </MatrixProvider>
          </CompanyProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

interface StyleOverrideProps {
  theme: Theme;
}

const StyleOverride: React.FC<StyleOverrideProps> = ({theme}) => (
  <GlobalStyles
    styles={{
      body: {
        '.MuiFormHelperText-root': {
          backgroundColor: theme.palette.background.default,
          boxSizing: 'border-box',
          margin: 0,
          padding: 0
        }
      }
    }}
  />
);

export default App;
